/**
 * VedetteLayer.tsx — Marker vedette L.A.C.E.S. sulla mappa SI.TA.C.
 *
 * Sprint 2 — check-in timer, trigger di allerta, multi-vedetta.
 *
 * © 2024-2025 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React from 'react';
import { Marker, Polygon, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { Lookout } from '../../types';
import { checkinCountdown } from '../../hooks/useCheckinMonitor';

// ── CSS animazione pulsante per marker overdue (iniettato una volta) ──
(function injectVedetteStyles() {
  if (document.getElementById('dos518-vedette-styles')) return;
  const s = document.createElement('style');
  s.id = 'dos518-vedette-styles';
  s.textContent = `
    @keyframes vedetta-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7), 0 2px 10px rgba(0,0,0,0.55); }
      50%      { box-shadow: 0 0 0 9px rgba(239,68,68,0), 0 2px 10px rgba(0,0,0,0.55); }
    }
    .vedetta-overdue-ring {
      animation: vedetta-pulse 1.1s ease-in-out infinite !important;
    }
  `;
  document.head.appendChild(s);
})();

// ── Colori stato operativo ─────────────────────────────────────
const STATO_COLOR: Record<string, string> = {
  in_posizione: '#22c55e',
  non_risponde: '#eab308',
  ritirata:     '#ef4444',
};

const STATO_LABEL: Record<string, string> = {
  in_posizione: '🟢 In posizione',
  non_risponde: '🟡 Non risponde',
  ritirata:     '🔴 Ritirata',
};

// ── Badge livello esperienza ───────────────────────────────────
const EXP_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  primo_anno: { bg: '#fef9c3', fg: '#92400e', label: '🌱' },
  esperto:    { bg: '#dbeafe', fg: '#1e3a8a', label: '⭐' },
  dos:        { bg: '#d1fae5', fg: '#065f46', label: '🏅' },
};

// ── Nota affidabilità per il popup ─────────────────────────────
const EXP_AFFIDABILITA: Record<string, { testo: string; colore: string }> = {
  primo_anno: { testo: 'Supervisione consigliata', colore: '#92400e' },
  esperto:    { testo: 'Alta affidabilità',         colore: '#1e40af' },
  dos:        { testo: 'Massima affidabilità',      colore: '#065f46' },
};

// ── Icona DivIcon ──────────────────────────────────────────────
function buildVedettaIcon(v: Lookout, overdue: boolean): L.DivIcon {
  const color = overdue
    ? '#ef4444'
    : v.statoOperativo ? (STATO_COLOR[v.statoOperativo] ?? '#6366f1') : '#6366f1';

  // Badge alert/trigger (angolo in alto a destra)
  const alertBadge = overdue
    ? `<div style="position:absolute;top:-6px;right:-6px;background:#ef4444;color:#fff;font-size:10px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;font-weight:900;">!</div>`
    : (v.triggerAttivi?.length ?? 0) > 0
      ? `<div style="position:absolute;top:-6px;right:-6px;background:#f97316;color:#fff;font-size:9px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;font-weight:900;">⚠</div>`
      : '';

  // Badge esperienza (angolo in alto a sinistra)
  const exp = v.livelloEsperienza ? EXP_STYLE[v.livelloEsperienza] : null;
  const expBadge = exp
    ? `<div style="position:absolute;top:-5px;left:-5px;background:${exp.bg};color:${exp.fg};font-size:9px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid rgba(255,255,255,0.9);box-shadow:0 1px 3px rgba(0,0,0,0.3);">${exp.label}</div>`
    : '';

  // Badge canale radio (sotto al centro — visibile senza aprire nulla)
  const radioHtml = v.canalRadio
    ? `<div style="position:absolute;bottom:-18px;left:50%;transform:translateX(-50%);background:rgba(15,23,42,0.88);color:#e2e8f0;font-size:8.5px;padding:2px 6px;border-radius:3px;white-space:nowrap;pointer-events:none;font-weight:700;letter-spacing:0.3px;border:1px solid rgba(255,255,255,0.12);">${v.canalRadio}</div>`
    : '';

  // Badge dotazione (sotto al radio, o alla stessa quota se radio assente)
  const dotazioneBottom = v.canalRadio ? -33 : -18;
  const dotazioneIcons = (v.dotazione ?? [])
    .map((d) => d === 'Drone' ? '🚁' : d === 'Termocamera' ? '🌡️' : '📡')
    .join('');
  const dotazioneHtml = dotazioneIcons
    ? `<div style="position:absolute;bottom:${dotazioneBottom}px;left:50%;transform:translateX(-50%);background:rgba(15,23,42,0.88);color:#38bdf8;font-size:9px;padding:2px 5px;border-radius:3px;white-space:nowrap;pointer-events:none;font-weight:700;border:1px solid rgba(56,189,248,0.35);">${dotazioneIcons}</div>`
    : '';

  const html = `
    <div style="position:relative;width:34px;height:34px;">
      <div class="${overdue ? 'vedetta-overdue-ring' : ''}" style="
        width:34px;height:34px;
        background:${color};
        border:2.5px solid rgba(255,255,255,0.9);
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-size:17px;line-height:1;
        box-shadow:0 2px 10px rgba(0,0,0,0.55);
        user-select:none;
      ">🔭</div>
      ${alertBadge}${expBadge}${radioHtml}${dotazioneHtml}
    </div>`;

  return L.divIcon({ html, iconSize: [34, 34], iconAnchor: [17, 17], className: '' });
}

// ── Calcolo settore angolare ──────────────────────────────────
function computeSector(
  lat: number, lng: number,
  azimutDeg: number, ampiezzaDeg: number,
  radiusM = 280,
): [number, number][] {
  const n = 24;
  const halfW = ampiezzaDeg / 2;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const pts: [number, number][] = [[lat, lng]];
  for (let i = 0; i <= n; i++) {
    const a = ((azimutDeg - halfW + (ampiezzaDeg * i) / n) * Math.PI) / 180;
    pts.push([lat + (radiusM * Math.cos(a)) / 111320,
              lng + (radiusM * Math.sin(a)) / (111320 * cosLat)]);
  }
  pts.push([lat, lng]);
  return pts;
}

// ── Helper countdown label ────────────────────────────────────
function CountdownLabel({ v }: { v: Lookout }) {
  const cd = checkinCountdown(v.ultimoCheckin, v.checkinIntervalMin ?? 15);
  if (!cd) return null;
  if (cd.overdue) {
    return (
      <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '0.72rem' }}>
        ⚠️ Check-in scaduto da {Math.abs(cd.remainingMin)} min
      </div>
    );
  }
  const urgent = cd.remainingMin <= 5;
  return (
    <div style={{ color: urgent ? '#eab308' : '#22c55e', fontSize: '0.72rem' }}>
      ✅ Check-in tra {cd.remainingMin} min
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────
interface VedetteLayerProps {
  vedette: Lookout[];
  overdueIds: Set<number>;
  onMoveVedetta:       (id: number, lat: number, lng: number) => void;
  onUpdateStato:       (id: number, stato: 'in_posizione' | 'non_risponde' | 'ritirata') => void;
  onUpdateAzimut:      (id: number, azimut: number) => void;
  onUpdateAmpiezza:    (id: number, ampiezza: number) => void;
  onConfirmCheckin:    (id: number) => void;
  onToggleTrigger:     (id: number, trigger: string) => void;
  onOpenConeSettings?: (id: number) => void;
  readOnly?: boolean;
}

// ── Componente principale ──────────────────────────────────────
const VedetteLayer: React.FC<VedetteLayerProps> = ({
  vedette, overdueIds,
  onMoveVedetta, onUpdateStato, onUpdateAzimut, onUpdateAmpiezza,
  onConfirmCheckin, onToggleTrigger, onOpenConeSettings,
  readOnly = false,
}) => {
  return (
    <>
      {vedette.map((v) => {
        if (v.lat === undefined || v.lng === undefined) return null;

        const overdue  = overdueIds.has(v.id);
        const color    = overdue ? '#ef4444' : v.statoOperativo ? (STATO_COLOR[v.statoOperativo] ?? '#6366f1') : '#6366f1';
        const azimut   = v.settoreAzimut  ?? 0;
        const ampiezza = v.settoreAmpiezza ?? 90;
        const sector   = computeSector(v.lat, v.lng, azimut, ampiezza);
        const sectorColor = overdue ? '#ef4444' : color;

        return (
          <React.Fragment key={v.id}>
            {/* Settore di osservazione */}
            <Polygon
              positions={sector}
              pathOptions={{
                color: sectorColor, fillColor: sectorColor,
                fillOpacity: overdue ? 0.2 : 0.13,
                weight: overdue ? 2 : 1.5,
                opacity: overdue ? 0.75 : 0.55,
                dashArray: '5 4',
              }}
            />

            {/* Marker vedetta */}
            <Marker
              position={[v.lat, v.lng]}
              icon={buildVedettaIcon(v, overdue)}
              draggable={!readOnly}
              eventHandlers={{
                dragend(e) {
                  const { lat, lng } = e.target.getLatLng();
                  onMoveVedetta(v.id, lat, lng);
                },
              }}
            >
              <Popup minWidth={200} maxWidth={260}>
                <div style={{ fontFamily: 'system-ui,sans-serif', padding: '2px 0' }}>

                  {/* Header */}
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 2 }}>
                    🔭 {v.nominativo}
                  </div>
                  <div style={{ fontSize: '0.73rem', color: '#555', marginBottom: 6 }}>
                    {v.settore}
                  </div>

                  {/* Alert overdue */}
                  {overdue && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '4px 8px', marginBottom: 8 }}>
                      <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.78rem' }}>
                        🚨 Check-in SCADUTO
                      </div>
                      <CountdownLabel v={v} />
                    </div>
                  )}

                  {/* Check-in countdown (se non overdue) */}
                  {!overdue && v.ultimoCheckin && (
                    <div style={{ marginBottom: 6 }}>
                      <CountdownLabel v={v} />
                    </div>
                  )}

                  {/* Bottone Confermato */}
                  {!readOnly && v.stato === 'attiva' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onConfirmCheckin(v.id); }}
                      style={{
                        width: '100%', padding: '5px 8px', marginBottom: 8,
                        borderRadius: 5, border: 'none',
                        background: overdue ? '#dc2626' : '#059669',
                        color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                    >
                      ✅ Confermato (check-in)
                    </button>
                  )}

                  {/* Livello esperienza + affidabilità */}
                  {v.livelloEsperienza && (() => {
                    const exp = EXP_STYLE[v.livelloEsperienza!];
                    const aff = EXP_AFFIDABILITA[v.livelloEsperienza!];
                    return (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 8, padding: '4px 8px', borderRadius: 5,
                        background: exp.bg, border: `1px solid ${exp.bg}`,
                      }}>
                        <span style={{ fontSize: '14px' }}>{exp.label}</span>
                        <div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: exp.fg }}>
                            {v.livelloEsperienza === 'primo_anno' ? '1° anno AIB' : v.livelloEsperienza === 'esperto' ? 'Esperto AIB' : 'DOS'}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: aff.colore }}>{aff.testo}</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Stato operativo */}
                  <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: 3 }}>Stato operativo</div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {(['in_posizione', 'non_risponde', 'ritirata'] as const).map((s) => (
                      <button
                        key={s}
                        onClick={(e) => { e.stopPropagation(); onUpdateStato(v.id, s); }}
                        style={{
                          flex: 1, padding: '3px 4px', borderRadius: 4,
                          border: `1.5px solid ${STATO_COLOR[s]}`,
                          background: v.statoOperativo === s ? STATO_COLOR[s] : 'transparent',
                          color: v.statoOperativo === s ? '#fff' : STATO_COLOR[s],
                          fontSize: '11px', cursor: 'pointer', fontWeight: 600,
                        }}
                        title={STATO_LABEL[s]}
                      >
                        {s === 'in_posizione' ? '🟢' : s === 'non_risponde' ? '🟡' : '🔴'}
                      </button>
                    ))}
                  </div>

                  {/* Trigger attivi */}
                  {(v.triggerMonitorati?.length ?? 0) > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: 3 }}>Trigger allerta</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {v.triggerMonitorati!.map((t) => {
                          const attivo = v.triggerAttivi?.includes(t);
                          return (
                            <button
                              key={t}
                              onClick={(e) => { e.stopPropagation(); onToggleTrigger(v.id, t); }}
                              style={{
                                padding: '2px 6px', borderRadius: 3, fontSize: '9.5px', cursor: 'pointer',
                                border: `1px solid ${attivo ? '#f97316' : '#d1d5db'}`,
                                background: attivo ? '#fff7ed' : 'transparent',
                                color: attivo ? '#ea580c' : '#6b7280',
                                fontWeight: attivo ? 700 : 400,
                              }}
                              title={attivo ? 'Tocca per disattivare' : 'Tocca per segnalare'}
                            >
                              {attivo ? '🚨' : '○'} {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Cono — direzione e ampiezza in modale esterno (evita conflitti touch Leaflet) */}
                  {!readOnly && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: '0.7rem', color: '#555', marginBottom: 4 }}>
                        🧭 {azimut}° · ↔ {ampiezza}°
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenConeSettings?.(v.id); }}
                        style={{
                          width: '100%', padding: '6px 8px', borderRadius: 5,
                          border: `1.5px solid ${color}`, background: 'transparent',
                          color, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        🧭 Ruota / Regola cono
                      </button>
                    </div>
                  )}

                  {/* Info extra */}
                  {v.canalRadio && (
                    <div style={{ fontSize: '0.73rem', color: '#555' }}>📻 {v.canalRadio}</div>
                  )}
                  {(v.dotazione?.length ?? 0) > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
                      {v.dotazione!.map((d) => (
                        <span key={d} style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.35)', color: '#0284c7', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>
                          {d === 'Drone' ? '🚁' : d === 'Termocamera' ? '🌡️' : '📡'} {d}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '0.68rem', color: '#999', marginTop: 4 }}>
                    {v.lat!.toFixed(5)}, {v.lng!.toFixed(5)}
                  </div>
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default VedetteLayer;
